/**
 * Dashboard widget: today's pickup slots at a glance (counts + close countdowns).
 * Same pattern as CarrierCutoffsCard — react-query fetch + local 30s countdown tick.
 */

import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { CalendarClock } from 'lucide-react';
import { Badge } from '../ui/Badge';
import { getSlotBoard } from '../../services/pickupSlots.api';
import {
  getBaghdadOperationalDate,
  getSlotCloseBadgeVariant,
  getSlotCloseStatus,
} from '../../utils/pickupSlotStatus';

export default function PickupSlotsCard() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(t);
  }, []);

  const opDate = getBaghdadOperationalDate(now);
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['pickupSlotBoard', opDate],
    queryFn: () => getSlotBoard(opDate),
    refetchInterval: 60_000,
  });

  const isDisabled = (error as { response?: { status?: number } } | null)?.response?.status === 503;
  if (isDisabled) return null; // hide the widget entirely on dark environments

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CalendarClock className="h-5 w-5 text-blue-600" />
          <h3 className="font-semibold text-gray-900">Pickup Slots</h3>
          <span className="text-xs text-gray-500">{opDate}</span>
        </div>
        <Link to="/pickup-slots" className="text-xs text-blue-600 hover:underline">
          Manage →
        </Link>
      </div>
      {isLoading ? (
        <p className="mt-3 text-sm text-gray-500">Loading…</p>
      ) : isError || !data ? (
        <p className="mt-3 text-sm text-red-600">Unavailable</p>
      ) : (
        <div className="mt-3 space-y-1.5">
          {data.slots.map((slot) => {
            const status = getSlotCloseStatus(slot.close_instant, slot.closed_at, now);
            const routed = (slot.counts['route_included'] ?? 0) + (slot.counts['route_appended'] ?? 0);
            return (
              <div key={slot.code} className="flex items-center justify-between text-sm">
                <span className="font-medium text-gray-700">
                  {slot.code}
                  <span className="ml-1 text-xs font-normal text-gray-500">
                    {slot.window.from}–{slot.window.to}
                  </span>
                </span>
                <span className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">
                    {routed}/{slot.counts.total} routed
                  </span>
                  <Badge size="sm" variant={getSlotCloseBadgeVariant(status.state)}>
                    {status.label}
                  </Badge>
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
